import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, RotateCcw, Loader2 } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { formatNumber } from "@/lib/utils";

export interface RePrepressItem {
  id: string;
  productName: string;
  prepressCode?: string;
  quantity?: number;
  poReference?: string;
}

interface RePrepressRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: RePrepressItem[];
  onSuccess?: () => void;
}

const SUGGESTIONS = [
  "Lệch sản lượng ghép bài quá lớn, dôi dư cao",
  "Khổ in không phù hợp máy in khả dụng",
  "Khổ giấy không có sẵn trong kho, cần đổi khổ",
  "Sai hướng thớ giấy hoặc số UP chưa tối ưu",
  "Cần tách riêng bài in thay vì ghép chung",
];

export function RePrepressRequestModal({
  open,
  onOpenChange,
  items,
  onSuccess,
}: RePrepressRequestModalProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      toast.error("Vui lòng nhập lý do yêu cầu bình lại (tối thiểu 5 ký tự)");
      return;
    }

    if (items.length === 0) {
      toast.error("Không có chi tiết đơn hàng nào được chọn");
      return;
    }

    setIsSubmitting(true);
    try {
      const detailIds = items.map((i) => i.id);
      const res = await trpcClient.orders.requestRePrepress.mutate({
        detailIds,
        reason: trimmed,
      });

      const held: any[] = (res as any)?.heldOrders || [];
      toast.success(
        `Đã gửi yêu cầu bình lại cho ${(res as any)?.count || items.length} dòng sản phẩm.` +
          (held.length > 0 ? ` Lệnh ${held.map((h: any) => h.orderId).join(", ")} được tạm giữ đến khi chế bản bình xong.` : "")
      );

      // Invalidate queries so tables refresh
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["order-for-lsx"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["pending-prepress-order-details"] }),
        queryClient.invalidateQueries({ queryKey: ["prepress-ready-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["production-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["production-order"] }),
        queryClient.invalidateQueries({ queryKey: ["order"] }),
      ]);

      setReason("");
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(error?.message || "Có lỗi xảy ra khi gửi yêu cầu bình lại");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Yêu Cầu Chế Bản Bình Lại (Re-Prepress)
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Các dòng đơn được yêu cầu bình lại sẽ chuyển sang trạng thái{" "}
            <strong className="text-rose-600 font-semibold">RE_PREPRESS</strong> và bị
            khóa không cho tạo Lệnh SX cho tới khi Chế bản xác nhận hoàn tất.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Selected items list */}
          <div>
            <Label className="text-xs font-semibold text-slate-700 block mb-1.5">
              Chi tiết sản phẩm yêu cầu bình lại ({items.length}):
            </Label>
            <div className="max-h-36 overflow-y-auto space-y-1.5 p-2 rounded-lg bg-slate-50 border border-slate-200">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between text-xs py-1 px-2 rounded bg-white border border-slate-100"
                >
                  <div className="min-w-0 pr-2">
                    <span className="font-semibold text-slate-800 break-words whitespace-normal block">
                      {item.productName}
                    </span>
                    {item.poReference && (
                      <span className="text-[11px] text-slate-400 font-mono">
                        PO: {item.poReference}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {item.prepressCode && (
                      <Badge variant="outline" className="text-[10px] font-mono text-indigo-700 bg-indigo-50 border-indigo-200">
                        {item.prepressCode}
                      </Badge>
                    )}
                    {item.quantity !== undefined && (
                      <span className="font-bold text-slate-700">
                        {formatNumber(item.quantity)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Suggested reasons */}
          <div>
            <Label className="text-xs text-slate-500 block mb-1.5">
              Gợi ý lý do nhanh:
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((sug, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setReason(sug)}
                  className="text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors text-left"
                >
                  {sug}
                </button>
              ))}
            </div>
          </div>

          {/* Reason input */}
          <div className="space-y-1.5">
            <Label htmlFor="reprepress-reason" className="text-xs font-semibold text-slate-700 flex items-center justify-between">
              <span>Lý do yêu cầu bình lại <span className="text-rose-500">*</span></span>
              <span className="text-[11px] text-slate-400 font-normal">Tối thiểu 5 ký tự</span>
            </Label>
            <Textarea
              id="reprepress-reason"
              rows={3}
              placeholder="Ví dụ: Khổ in 65x86 không vừa máy in 4 màu, cần bình lại sang khổ 54x79..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="text-xs leading-relaxed resize-none"
            />
          </div>

          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Bộ phận Chế bản sẽ nhận được thông báo tại tab{" "}
              <strong>Yêu Cầu Bình Lại</strong> của trang Chờ Bình Bài để cập nhật lại bài bình.
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Hủy
          </Button>
          <Button
            size="sm"
            className="bg-rose-600 hover:bg-rose-700 text-white"
            onClick={handleSubmit}
            disabled={isSubmitting || reason.trim().length < 5}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Đang gửi...
              </>
            ) : (
              <>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Gửi Yêu Cầu Bình Lại
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
